// System sizes as the page writes them: kWp for the DC array, kWac for the inverters.
import type { AmazonBuilding } from "../../api/hooks/useAmazonOverview";
import { sizeGap, type Tone } from "../../utils/sizeGap";

/** To a tenth at most, so a figure just under a threshold (999.9) never reads as on it. */
export const sizeText = (value: number | null, unit: "kWp" | "kWac"): string =>
  value == null
    ? ""
    : `${value.toLocaleString("en-GB", { maximumFractionDigits: 1 })} ${unit}`;

/** A portfolio total in MW to two decimals ("6.05 MWac"), from its kW. */
export const mwText = (kw: number, unit: "MWp" | "MWac"): string =>
  `${(kw / 1000).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;

/** A size from its lowest to its highest, the same figure twice where it is one. */
export interface Span {
  min: number;
  max: number;
}

export const one = (x: number): Span => ({ min: x, max: x });

const figure = (x: number): string => x.toLocaleString("en-GB", { maximumFractionDigits: 1 });

/** "1,800-2,340 kWp", or one figure where the span is one. */
export const spanText = (span: Span, unit: "kWp" | "kWac"): string =>
  span.min === span.max
    ? sizeText(span.max, unit)
    : `${figure(span.min)}-${sizeText(span.max, unit)}`;

/** A size's kWp and kWac, either a span, with whichever is known. */
export const pairParts = (kwp: Span | null, kwAc: Span | null): string[] =>
  [kwp && spanText(kwp, "kWp"), kwAc && spanText(kwAc, "kWac")].filter(
    (x): x is string => !!x,
  );

/** A size as "X kWp / Y kWac". */
const pairText = (kwp: Span | null, kwAc: Span | null): string =>
  pairParts(kwp, kwAc).join(" / ") || "-";

export const ppaText = (b: AmazonBuilding): string =>
  pairText(
    b.committed_kwp == null ? null : one(b.committed_kwp),
    b.committed_kw_ac == null ? null : one(b.committed_kw_ac),
  );

/** How far one achievable figure falls short of the PPA's: red for a big gap (the
 * dashboard's rule), amber for any smaller shortfall, nothing when it meets the PPA. */
export const ppaShortfall = (
  achievable: number | null,
  ppa: number | null,
): Tone | undefined =>
  achievable != null && ppa != null && achievable < ppa
    ? sizeGap(achievable, ppa) === "red"
      ? "red"
      : "amber"
    : undefined;
